import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle 
} from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { LoadingSpinner } from '../ui/loading-spinner';
import { useBlogPosts } from '../../hooks/useBlogPosts';
import { 
  Plus, 
  Edit2, 
  Trash2, 
  ArrowLeft, 
  Tag, 
  FileText, 
  AlertCircle 
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const CategoryManager = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { 
    categories, 
    loading, 
    error, 
    fetchCategoriesWithCounts, // Use admin version with accurate counts
    createCategory,
    updateCategory,
    deleteCategory,
    clearError 
  } = useBlogPosts();

  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [deletingCategory, setDeletingCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    slug: ''
  });
  const [formLoading, setFormLoading] = useState(false);

  useEffect(() => {
    fetchCategoriesWithCounts();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Remove dependency since we only want to run once on mount

  const handleCreateCategory = async () => {
    if (!formData.name.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Category name is required.',
        variant: 'destructive'
      });
      return;
    }

    try {
      setFormLoading(true);
      await createCategory({
        name: formData.name.trim(),
        description: formData.description.trim(),
        slug: formData.slug.trim() || generateSlug(formData.name)
      });
      
      toast({
        title: 'Success',
        description: 'Category created successfully.',
      });
      
      setShowCreateDialog(false);
      resetForm();
    } catch (err) {
      console.error('Error creating category:', err);
      toast({
        title: 'Error',
        description: 'Failed to create category. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setFormLoading(false);
    }
  };

  const handleEditCategory = async () => {
    if (!formData.name.trim() || !editingCategory) {
      return;
    }

    try {
      setFormLoading(true);
      await updateCategory(editingCategory.id, {
        name: formData.name.trim(),
        description: formData.description.trim(),
        slug: formData.slug.trim() || generateSlug(formData.name)
      });
      
      toast({
        title: 'Success',
        description: 'Category updated successfully.',
      });
      
      setShowEditDialog(false);
      resetForm();
      setEditingCategory(null);
    } catch (err) {
      console.error('Error updating category:', err);
      toast({
        title: 'Error',
        description: 'Failed to update category. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteCategory = async () => {
    if (!deletingCategory) return;

    try {
      setFormLoading(true);
      await deleteCategory(deletingCategory.id);
      
      toast({
        title: 'Success',
        description: 'Category deleted successfully.',
      });
      
      setShowDeleteDialog(false);
      setDeletingCategory(null);
    } catch (err) {
      console.error('Error deleting category:', err);
      toast({
        title: 'Error',
        description: 'Failed to delete category. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setFormLoading(false);
    }
  };

  const openEditDialog = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      description: category.description || '',
      slug: category.slug || ''
    });
    setShowEditDialog(true);
  };

  const openDeleteDialog = (category) => {
    setDeletingCategory(category);
    setShowDeleteDialog(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      slug: ''
    });
  };

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleSlugChange = (value) => {
    const cleanSlug = value
      .toLowerCase()
      .replace(/[^a-z0-9-]/g, '')
      .replace(/^-+|-+$/g, '');
    setFormData(prev => ({ ...prev, slug: cleanSlug }));
  };

  if (loading && !categories.length) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="max-w-4xl mx-auto">
          <div className="flex justify-center items-center py-12">
            <LoadingSpinner />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/admin/blog')}
              className="flex items-center gap-2"
            >
              <ArrowLeft size={16} />
              Back to Blog Dashboard
            </Button>
          </div>
          <Button onClick={() => setShowCreateDialog(true)}>
            <Plus size={16} className="mr-2" />
            Add Category
          </Button>
        </div>

        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Blog Categories</h1>
          <p className="text-gray-600 mt-2">
            Organize your blog content with categories.
          </p>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  Error loading categories
                </h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
                <div className="mt-4">
                  <button
                    onClick={() => {
                      clearError();
                      fetchCategoriesWithCounts();
                    }}
                    className="text-sm bg-red-100 text-red-800 px-3 py-1 rounded-md hover:bg-red-200 transition-colors"
                  >
                    Try Again
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Categories Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {categories.map((category) => (
            <Card key={category.id} className="relative">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <Tag size={20} className="text-purple-600" />
                    <CardTitle className="text-lg text-gray-900">{category.name}</CardTitle>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEditDialog(category)}
                      className="text-gray-600 hover:text-gray-900"
                    >
                      <Edit2 size={16} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openDeleteDialog(category)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </div>
                {category.slug && (
                  <Badge variant="secondary" className="w-fit text-gray-700 bg-gray-100">
                    /{category.slug}
                  </Badge>
                )}
              </CardHeader>
              
              <CardContent>
                {category.description && (
                  <p className="text-gray-600 text-sm mb-3">
                    {category.description}
                  </p>
                )}
                
                <div className="flex items-center gap-4 text-sm text-gray-500">
                  <div className="flex items-center gap-1">
                    <FileText size={14} />
                    <span>{category.postCount || 0} posts</span>
                  </div>
                  <div>
                    Created {category.createdAt && new Date(category.createdAt.seconds * 1000).toLocaleDateString()}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {categories.length === 0 && !loading && (
          <Card className="text-center py-12">
            <CardContent>
              <Tag size={48} className="text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No categories yet
              </h3>
              <p className="text-gray-600 mb-4">
                Create your first category to start organizing your blog content.
              </p>
              <Button onClick={() => setShowCreateDialog(true)}>
                <Plus size={16} className="mr-2" />
                Create Category
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Create Category Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Category</DialogTitle>
              <DialogDescription>
                Add a new category to organize your blog posts.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="category-name" className="text-gray-700">Name *</Label>
                <Input
                  id="category-name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    name: e.target.value 
                  }))}
                  placeholder="Enter category name"
                />
              </div>
              
              <div>
                <Label htmlFor="category-slug" className="text-gray-700">Slug</Label>
                <Input
                  id="category-slug"
                  value={formData.slug}
                  onChange={(e) => handleSlugChange(e.target.value)}
                  placeholder="category-slug (auto-generated if empty)"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Used in URLs. Leave empty to auto-generate from name.
                </p>
              </div>
              
              <div>
                <Label htmlFor="category-description" className="text-gray-700">Description</Label>
                <Textarea
                  id="category-description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    description: e.target.value 
                  }))}
                  placeholder="Optional description"
                  rows={3}
                />
              </div>
            </div>

            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowCreateDialog(false);
                  resetForm();
                }}
                disabled={formLoading}
              >
                Cancel
              </Button>
              <Button onClick={handleCreateCategory} disabled={formLoading}>
                {formLoading ? (
                  <>
                    <LoadingSpinner size="sm" className="mr-2" />
                    Creating...
                  </>
                ) : (
                  'Create Category'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Category Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Category</DialogTitle>
              <DialogDescription>
                Update the category information.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="edit-category-name" className="text-gray-700">Name *</Label>
                <Input
                  id="edit-category-name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    name: e.target.value 
                  }))}
                  placeholder="Enter category name"
                />
              </div>
              
              <div>
                <Label htmlFor="edit-category-slug" className="text-gray-700">Slug</Label>
                <Input
                  id="edit-category-slug"
                  value={formData.slug}
                  onChange={(e) => handleSlugChange(e.target.value)}
                  placeholder="category-slug"
                />
              </div>
              
              <div>
                <Label htmlFor="edit-category-description" className="text-gray-700">Description</Label>
                <Textarea
                  id="edit-category-description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    description: e.target.value 
                  }))}
                  placeholder="Optional description"
                  rows={3}
                />
              </div>
            </div>

            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowEditDialog(false);
                  resetForm();
                  setEditingCategory(null);
                }}
                disabled={formLoading}
              >
                Cancel
              </Button>
              <Button onClick={handleEditCategory} disabled={formLoading}>
                {formLoading ? (
                  <>
                    <LoadingSpinner size="sm" className="mr-2" />
                    Updating...
                  </>
                ) : (
                  'Update Category'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Category</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete "{deletingCategory?.name}"? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>

            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowDeleteDialog(false);
                  setDeletingCategory(null);
                }}
                disabled={formLoading}
              >
                Cancel
              </Button>
              <Button 
                variant="destructive" 
                onClick={handleDeleteCategory} 
                disabled={formLoading}
              >
                {formLoading ? (
                  <>
                    <LoadingSpinner size="sm" className="mr-2" />
                    Deleting...
                  </>
                ) : (
                  'Delete Category'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default CategoryManager;